import { Component, OnInit, Input } from '@angular/core';

/**
 * Union type defining the two available logo display variants.
 *
 * @remarks
 * Use `'full'` to display the complete company logo with text branding.
 * Use `'emblem'` to display only the visual emblem symbol without text.
 */
export type LogoType = 'full' | 'emblem';

/**
 * Component that displays the Backbase company logo with configurable styling.
 *
 * @remarks
 * The logo component renders the company logo as a styled `<div>` element with CSS classes
 * applied dynamically based on the configured type and color scheme. The component supports
 * two display variants (full logo with text or emblem-only) and an inverse color mode for
 * use on dark backgrounds. The logo is hidden from screen readers via `aria-hidden="true"`.
 *
 * @example
 *
 * ```html
 * <bb-logo-ui type="full"></bb-logo-ui>
 * ```
 */
@Component({
  selector: 'bb-logo-ui',
  templateUrl: './logo.component.html',
  standalone: false,
})
export class LogoComponent implements OnInit {
  /**
   * The display variant of the logo.
   *
   * @remarks
   * Set to `'full'` to display the complete company logo with text branding.
   * Set to `'emblem'` to display only the visual emblem symbol.
   * Defaults to `'full'`.
   *
   * @example
   *
   * ```html
   * <bb-logo-ui type="emblem" class="bb-d-md-none"></bb-logo-ui>
   * ```
   */
  @Input() type: LogoType = 'full';

  /**
   * Flag to enable inverse color styling for display on dark backgrounds.
   *
   * @remarks
   * When `true`, applies the inverse CSS class to render the logo in lighter colors
   * suitable for dark or colored backgrounds. Defaults to `false`.
   *
   * @example
   *
   * ```html
   * <bb-logo-ui [isInverse]="true"></bb-logo-ui>
   * ```
   */
  @Input() isInverse = false;

  /**
   * Computed property indicating whether the component should render the emblem-only variant.
   *
   * @remarks
   * Returns `true` when `type === 'emblem'`, used internally by the template
   * to apply conditional CSS classes.
   *
   * @returns `true` if the logo type is set to `'emblem'`, otherwise `false`.
   */
  get isEmblem() {
    return this.type === 'emblem';
  }

  constructor() {}

  ngOnInit() {}
}
